import { cn } from "@qn/ui";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown, Image as ImageIcon, MessageSquareText, Wand2 } from "lucide-react";
import { useT } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";

const MODES = [
  { id: "auto", icon: Wand2 },
  { id: "text", icon: MessageSquareText },
  { id: "image", icon: ImageIcon },
] as const;

/** Next to the model picker: Auto (the app decides: image, document or chat), Text only, or Image for every message. */
export function ModePicker() {
  const t = useT();
  const mode = usePrefs((s) => s.sendMode);
  const set = usePrefs((s) => s.set);
  const cur = MODES.find((m) => m.id === mode) ?? MODES[0];
  const Icon = cur.icon;
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button
          type="button"
          aria-label={t("mode.title")}
          className="flex items-center gap-1.5 rounded-xl border border-[var(--line)] bg-[var(--surface)] px-2.5 py-1.5 text-sm font-medium transition hover:border-[var(--line-strong)] hover:bg-[var(--surface-2)]"
        >
          <Icon className="size-4 text-[var(--accent-text)]" />
          <span className="hidden sm:inline">{t(`mode.${cur.id}`)}</span>
          <ChevronDown className="size-4 text-[var(--fg-faint)]" />
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          align="start"
          sideOffset={6}
          className="glass z-50 w-64 rounded-xl border border-[var(--line)] p-1.5 shadow-[var(--shadow)]"
        >
          {MODES.map((m) => (
            <Dropdown.Item
              key={m.id}
              onSelect={() => set({ sendMode: m.id })}
              className="flex cursor-pointer items-start gap-2 rounded-lg px-2.5 py-2 text-sm outline-none data-[highlighted]:bg-[var(--surface-3)]"
            >
              <m.icon className="mt-0.5 size-4 shrink-0 text-[var(--fg-muted)]" />
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{t(`mode.${m.id}`)}</span>
                <span className="block text-xs text-[var(--fg-muted)]">
                  {t(`mode.${m.id}.hint`)}
                </span>
              </span>
              <Check
                className={cn(
                  "size-4 text-[var(--accent-text)]",
                  mode === m.id ? "opacity-100" : "opacity-0",
                )}
              />
            </Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
